import { clsx } from 'clsx';
import type { PrioriteDeneignement } from '@/types';
import { PRIORITE_LABELS } from '@/types';
import {
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  MinusCircleIcon,
} from '@heroicons/react/24/solid';

interface PrioriteBadgeProps {
  priorite: PrioriteDeneignement;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
}

const PRIORITE_STYLES: Record<PrioriteDeneignement, { bg: string; text: string; icon: typeof ExclamationTriangleIcon }> = {
  critique: {
    bg: 'bg-red-100',
    text: 'text-red-700',
    icon: ExclamationTriangleIcon,
  },
  urgente: {
    bg: 'bg-orange-100',
    text: 'text-orange-700',
    icon: ExclamationCircleIcon,
  },
  normale: {
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    icon: MinusCircleIcon,
  },
};

/**
 * Badge affichant la priorite d'une intervention
 * SFD v2.1: Indicateur visuel avec couleurs et icones distinctes
 */
export function PrioriteBadge({ priorite, size = 'md', showIcon = true, className }: PrioriteBadgeProps) {
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base',
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  const style = PRIORITE_STYLES[priorite];
  const Icon = style.icon;

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 font-medium rounded-full',
        style.bg,
        style.text,
        sizeClasses[size],
        className
      )}
    >
      {showIcon && <Icon className={iconSizes[size]} />}
      {PRIORITE_LABELS[priorite]}
    </span>
  );
}

export default PrioriteBadge;
